import { useCallback, useEffect, useMemo, useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import DebouncedSearchField from "components/DebouncedSearchField/DebouncedSearchField";
import PatientProgressTable from "components/Tables/PatientProgressTable/PatientProgressTable";
import PatientProgressHelpModal from "components/Modals/PatientProgressHelpModal/PatientProgressHelpModal";
import {
  IPatientProgressFilters,
  ITestExecutor,
} from "interfaces/Patient";
import { OrderDirection, PageSize } from "enums/Common";
import {
  DateRange,
  OrderTypeFilter,
  PatientFilter,
  ReviewStatusFilter,
} from "enums/Patient";
import PatientService from "services/PatientService";
import UserService from "services/UserService";
import moment from "moment";
import styles from "./PatientProgress.module.scss";

interface IOrderingClinician {
  id: string;
  firstName: string;
  lastName: string;
  isInactive: boolean;
}

interface PatientProgressProps {
  clinicianId?: string;
  applyNotAccessedFilter?: number;
}

const dateRangeOptions = [
  { value: DateRange.All, label: "All" },
  { value: DateRange.Last2Days, label: "Last 2 test days" },
  { value: DateRange.Last30Days, label: "Last 30 days" },
  { value: DateRange.Last60Days, label: "Last 60 days" },
  { value: DateRange.Last90Days, label: "Last 90 days" },
];

const orderTypeOptions = [
  { value: OrderTypeFilter.All, label: "All" },
  { value: OrderTypeFilter.InClinicOnly, label: "In Clinic Only" },
  { value: OrderTypeFilter.RkmPatients, label: "RKM Patients" },
  { value: OrderTypeFilter.RkmCurrent, label: "RKM Patients - Current" },
  { value: OrderTypeFilter.RkmExpired, label: "RKM Patients - Expired" },
];

const reviewStatusOptions = [
  { value: ReviewStatusFilter.All, label: "All" },
  {
    value: ReviewStatusFilter.NotAccessed30Days,
    label: "Not Accessed Past 30 days",
  },
];

const PatientProgress = ({ clinicianId, applyNotAccessedFilter }: PatientProgressProps) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  // Filter UI state
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.All);
  const [orderType, setOrderType] = useState<OrderTypeFilter>(
    OrderTypeFilter.All
  );
  const [reviewStatus, setReviewStatus] = useState<ReviewStatusFilter>(
    ReviewStatusFilter.All
  );
  const [selectedClinicianId, setSelectedClinicianId] = useState<string>(
    clinicianId ?? ""
  );
  const [selectedExecutor, setSelectedExecutor] =
    useState<ITestExecutor | null>(null);

  // Dropdown data
  const [clinicians, setClinicians] = useState<IOrderingClinician[]>([]);
  const [testExecutors, setTestExecutors] = useState<ITestExecutor[]>([]);

  // API filters (pagination, sorting, search)
  const [filters, setFilters] = useState<IPatientProgressFilters>({
    _total_count: 1,
    page: 1,
    page_size: PageSize.Default,
    _order_by: `last_name ${OrderDirection.Asc}`,
    clinicianId,
  });

  // Fetch ordering clinicians and test executors on mount
  useEffect(() => {
    const controller = new AbortController();

    if (!clinicianId) {
      PatientService.getOrderingClinicians(controller.signal)
        .then(({ data }) => setClinicians(data ?? []))
        .catch(() => {});
    }

    UserService.getTestExecutors(controller.signal)
      .then(({ data }) => setTestExecutors(data ?? []))
      .catch(() => {}); // silently handle — autocomplete will just be empty

    return () => controller.abort();
  }, [clinicianId]);

  // Combine UI filter state into API filters
  const apiFilters = useMemo((): IPatientProgressFilters => {
    const result: IPatientProgressFilters = { ...filters };

    // Date range
    switch (dateRange) {
      case DateRange.Last2Days:
        result.last_n_test_days = 2;
        break;
      case DateRange.Last30Days:
        result.from = moment().subtract(30, "days").format("YYYY-MM-DD");
        break;
      case DateRange.Last60Days:
        result.from = moment().subtract(60, "days").format("YYYY-MM-DD");
        break;
      case DateRange.Last90Days:
        result.from = moment().subtract(90, "days").format("YYYY-MM-DD");
        break;
    }

    // Order type
    switch (orderType) {
      case OrderTypeFilter.InClinicOnly:
        result.order_type = 1;
        break;
      case OrderTypeFilter.RkmPatients:
        result.order_type = 2;
        break;
      case OrderTypeFilter.RkmCurrent:
        result.order_type = 2;
        result.order_expired = false;
        break;
      case OrderTypeFilter.RkmExpired:
        result.order_type = 2;
        result.order_expired = true;
        break;
    }

    // Review status
    if (reviewStatus === ReviewStatusFilter.NotAccessed30Days) {
      result.not_accessed = true;
    }

    // Clinician
    if (selectedClinicianId) {
      result.clinicianId = selectedClinicianId;
    }

    // Tests run by
    if (selectedExecutor) {
      result.created_by_id = selectedExecutor.id;
    }

    return result;
  }, [
    filters,
    dateRange,
    orderType,
    reviewStatus,
    selectedClinicianId,
    selectedExecutor,
  ]);

  const resetFilters = useCallback(() => {
    setDateRange(DateRange.All);
    setOrderType(OrderTypeFilter.All);
    setReviewStatus(ReviewStatusFilter.All);
    setSelectedClinicianId(clinicianId ?? "");
    setSelectedExecutor(null);
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [PatientFilter.NameOrId]: undefined,
    }));
  }, [clinicianId]);

  // Apply "Not Accessed" preset filters when triggered from the widget
  useEffect(() => {
    if (applyNotAccessedFilter) {
      setOrderType(OrderTypeFilter.RkmPatients);
      setReviewStatus(ReviewStatusFilter.NotAccessed30Days);
      setFilters((prev) => ({
        ...prev,
        page: 1,
        [PatientFilter.NameOrId]: undefined,
      }));
    }
  }, [applyNotAccessedFilter, clinicianId]);

  const hasActiveFilters =
    dateRange !== DateRange.All ||
    orderType !== OrderTypeFilter.All ||
    reviewStatus !== ReviewStatusFilter.All ||
    selectedClinicianId !== (clinicianId ?? "") ||
    selectedExecutor !== null ||
    !!filters[PatientFilter.NameOrId];

  const updatePage = useCallback(
    () => setFilters((prev) => ({ ...prev, page: 1 })),
    []
  );

  return (
    <div className="flex flex-col p-5 rounded-lg bg-white w-full grow">
      <div className="flex flex-col gap-3 mb-3">
        {/* Title row */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <p className="text-lg font-bold uppercase">Patient Progress</p>
            <button
              onClick={() => setIsHelpModalOpen(true)}
              className="flex items-center justify-center"
            >
              <InfoIcon className="text-malibu !w-5 !h-5 cursor-pointer hover:opacity-80 transition-opacity" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <DebouncedSearchField
              className={`${styles.search_field}`}
              placeholder="Search by Patient Name or ID"
              value={filters[PatientFilter.NameOrId]}
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  page: 1,
                  [PatientFilter.NameOrId]: value,
                }))
              }
            />
          </div>
        </div>

        {/* Filter dropdowns row */}
        <div className="flex flex-wrap items-end gap-3">
          {/* Date Range */}
          <div className={styles.filter_dropdown}>
            <label className="text-xs text-fiord font-medium">Date Range</label>
            <Select
              size="small"
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value as DateRange);
                updatePage();
              }}
            >
              {dateRangeOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Order Type */}
          <div className={styles.filter_dropdown}>
            <label className="text-xs text-fiord font-medium">Order Type</label>
            <Select
              size="small"
              value={orderType}
              onChange={(e) => {
                setOrderType(e.target.value as OrderTypeFilter);
                updatePage();
              }}
            >
              {orderTypeOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Record Review Status */}
          <div className={styles.filter_dropdown}>
            <label className="text-xs text-fiord font-medium">
              Record Review Status
            </label>
            <Select
              size="small"
              value={reviewStatus}
              onChange={(e) => {
                setReviewStatus(e.target.value as ReviewStatusFilter);
                updatePage();
              }}
            >
              {reviewStatusOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Ordering Clinician (only show if not already scoped to a clinician) */}
          {!clinicianId && (
            <div className={styles.filter_dropdown}>
              <label className="text-xs text-fiord font-medium">
                Ordering Clinician
              </label>
              <Select
                size="small"
                value={selectedClinicianId}
                displayEmpty
                onChange={(e) => {
                  setSelectedClinicianId(e.target.value);
                  updatePage();
                }}
              >
                <MenuItem value="">All</MenuItem>
                {clinicians.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.lastName}, {c.firstName}
                    {c.isInactive && <span className="text-fiord/50 ml-1">(inactive)</span>}
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}

          {/* Tests Run By */}
          <div className={styles.filter_dropdown}>
            <label className="text-xs text-fiord font-medium">
              Tests Run By
            </label>
            <Autocomplete
              size="small"
              options={[{ id: "", firstName: "All", lastName: "" } as ITestExecutor, ...testExecutors]}
              getOptionLabel={(option) =>
                option.id === "" ? "All" : `${option.lastName}, ${option.firstName}`
              }
              value={selectedExecutor}
              onChange={(_, value) => {
                setSelectedExecutor(value?.id === "" ? null : value);
                updatePage();
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="All" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              componentsProps={{ popper: { style: { minWidth: "250px" }, placement: "bottom-end" } }}
            />
          </div>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="text-sm text-white bg-pelorous rounded px-3 py-1.5 font-medium hover:opacity-80 transition-opacity whitespace-nowrap"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      <PatientProgressTable filters={apiFilters} setFilters={setFilters} />

      {isHelpModalOpen && (
        <PatientProgressHelpModal onClose={() => setIsHelpModalOpen(false)} />
      )}
    </div>
  );
};

export default PatientProgress;
